// Vehicle picker overlay — shutter-style panel that slides from header edge
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { useSettingsStore } from '@/store/settingsStore';
import { getVehicles } from '@/services/api';
import type { Vehicle } from '@/types';

interface VehiclePickerOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export function VehiclePickerOverlay({ isOpen, onClose }: VehiclePickerOverlayProps) {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMake, setActiveMake] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const {
    vehicleScope,
    setScopeToAll,
    setScopeToMake,
    setScopeToProject,
    searchBarPosition,
  } = useSettingsStore();

  // Shutter direction: slides from the same edge as the header
  const fromTop = searchBarPosition !== 'bottom';

  // Load vehicles on open
  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    getVehicles()
      .then(setVehicles)
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => searchInputRef.current?.focus(), 350);
      return () => clearTimeout(timer);
    }
    setSearchQuery('');
    setActiveMake(null);
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  // Unique makes for filter chips
  const makes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const v of vehicles) {
      counts.set(v.make, (counts.get(v.make) || 0) + 1);
    }
    return Array.from(counts.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([make, count]) => ({ make, count }));
  }, [vehicles]);

  // Filter vehicles by search query + active make filter
  const filteredVehicles = useMemo(() => {
    let result = vehicles;
    if (activeMake) {
      result = result.filter(v => v.make === activeMake);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(v =>
        v.name.toLowerCase().includes(q) ||
        v.make.toLowerCase().includes(q) ||
        v.model.toLowerCase().includes(q) ||
        String(v.year).includes(q)
      );
    }
    return result;
  }, [vehicles, searchQuery, activeMake]);

  const handleSelectVehicle = useCallback((vehicle: Vehicle) => {
    setScopeToProject(vehicle.id, vehicle.name, vehicle.make, vehicle.model);
    onClose();
  }, [setScopeToProject, onClose]);

  const handleSelectAll = useCallback(() => {
    setScopeToAll();
    onClose();
  }, [setScopeToAll, onClose]);

  const handleSelectMake = useCallback((make: string) => {
    setScopeToMake(make);
    onClose();
  }, [setScopeToMake, onClose]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="vehicle-picker"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed inset-0 z-50"
          onClick={onClose}
        >
          {/* Shutter panel — full-height dark glass */}
          <motion.div
            initial={{ y: fromTop ? '-100%' : '100%' }}
            animate={{ y: 0 }}
            exit={{ y: fromTop ? '-100%' : '100%' }}
            transition={{
              type: 'spring',
              damping: 32,
              stiffness: 300,
              mass: 0.8,
            }}
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-0 flex flex-col bg-black/60 backdrop-blur-xl"
          >

            {/* Header bar */}
            <div className="shrink-0 px-6 pt-4 pb-4 border-b border-workshop-border/50">
              <div className="max-w-7xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <img
                    src="/vm-logo-icon.svg"
                    alt="Ventura Machina"
                    className="h-9 w-auto opacity-60"
                  />
                  <h1 className="text-xl font-semibold text-white">Select a Project</h1>
                  <span className="text-sm text-workshop-text-muted">
                    {vehicles.length} vehicles
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSelectAll}
                    className={clsx(
                      'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                      vehicleScope.type === 'all'
                        ? 'bg-workshop-accent text-black'
                        : 'bg-workshop-surface border border-workshop-border text-workshop-text-muted hover:text-white hover:border-workshop-accent'
                    )}
                  >
                    All Projects
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-lg text-workshop-text-muted hover:text-white hover:bg-workshop-surface transition-colors"
                    title="Close (Esc)"
                  >
                    <CloseIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Search + Make filter chips */}
            <div className="shrink-0 px-6 pt-4 pb-3">
              <div className="max-w-7xl mx-auto space-y-3">
                {/* Search bar */}
                <div className="relative">
                  <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-workshop-text-muted" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search vehicles by name, make, model, or year..."
                    className="w-full pl-11 pr-4 py-3 bg-workshop-surface border border-workshop-border rounded-xl text-white placeholder:text-workshop-text-muted focus:outline-none focus:border-workshop-accent transition-colors"
                  />
                </div>

                {/* Make filter chips */}
                {makes.length > 1 && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => setActiveMake(null)}
                      className={clsx(
                        'px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150',
                        activeMake === null
                          ? 'bg-workshop-accent text-black'
                          : 'bg-workshop-surface border border-workshop-border text-workshop-text-muted hover:text-white hover:border-workshop-accent'
                      )}
                    >
                      All Makes
                    </button>

                    {makes.map(({ make, count }) => (
                      <div key={make} className="flex items-center">
                        <button
                          onClick={() => setActiveMake(activeMake === make ? null : make)}
                          className={clsx(
                            'px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-150',
                            activeMake === make
                              ? 'bg-workshop-accent text-black'
                              : 'bg-workshop-surface border border-workshop-border text-workshop-text-muted hover:text-white hover:border-workshop-accent'
                          )}
                        >
                          {make}
                          <span className={clsx(
                            'ml-1.5 text-xs',
                            activeMake === make ? 'text-black/60' : 'text-workshop-text-muted'
                          )}>
                            {count}
                          </span>
                        </button>
                        {activeMake === make && (
                          <button
                            onClick={() => handleSelectMake(make)}
                            className="ml-1 px-2 py-1.5 rounded-lg text-xs text-workshop-accent bg-workshop-accent/10 hover:bg-workshop-accent/20 transition-colors"
                            title={`Scope to all ${make}`}
                          >
                            Select All {make}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Vehicle grid — scrollable area */}
            <div className="flex-1 overflow-auto px-6 pb-6">
              <div className="max-w-7xl mx-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-8 h-8 border-2 border-workshop-accent border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : filteredVehicles.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <CarIcon className="w-16 h-16 text-workshop-text-muted/30 mb-4" />
                    <p className="text-workshop-text-muted text-lg">
                      {searchQuery || activeMake ? 'No vehicles match your filters' : 'No vehicles found'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 pt-2">
                    {filteredVehicles.map((vehicle, i) => (
                      <VehicleCard
                        key={vehicle.id}
                        vehicle={vehicle}
                        index={i}
                        isSelected={vehicleScope.type === 'project' && vehicleScope.projectId === vehicle.id}
                        onClick={() => handleSelectVehicle(vehicle)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function VehicleCard({
  vehicle,
  index,
  isSelected,
  onClick,
}: {
  vehicle: Vehicle;
  index: number;
  isSelected: boolean;
  onClick: () => void;
}) {
  const [imgError, setImgError] = useState(false);

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.05, 0.5), ease: [0.25, 0.46, 0.45, 0.94] }}
      onClick={onClick}
      className={clsx(
        'group relative w-full text-left rounded-xl overflow-hidden',
        'bg-workshop-surface border-2 transition-all duration-200',
        'hover:border-workshop-accent hover:shadow-xl hover:shadow-workshop-accent/10 hover:-translate-y-0.5',
        'focus:outline-none focus:border-workshop-accent',
        isSelected
          ? 'border-workshop-accent shadow-lg shadow-workshop-accent/20'
          : 'border-workshop-border'
      )}
    >
      {/* Hero image */}
      <div className="aspect-[16/10] relative overflow-hidden bg-workshop-bg">
        {vehicle.heroImageUrl && !imgError ? (
          <img
            src={vehicle.heroImageUrl}
            alt={vehicle.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-workshop-surface to-workshop-bg">
            <CarIcon className="w-16 h-16 text-workshop-text-muted/20" />
          </div>
        )}

        {/* Gradient overlay at bottom for text readability */}
        <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 to-transparent" />

        {/* Vehicle name overlay on image */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white font-semibold text-lg leading-tight drop-shadow-md">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </p>
        </div>

        {/* Selected check */}
        {isSelected && (
          <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-workshop-accent flex items-center justify-center shadow-lg">
            <CheckIcon className="w-4 h-4 text-black" />
          </div>
        )}
      </div>

      {/* Info bar below image */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-workshop-text-muted text-sm">
          <CameraIcon className="w-4 h-4" />
          <span>{vehicle.imageCount} images</span>
        </div>
        {isSelected && (
          <span className="text-xs text-workshop-accent font-medium">Current</span>
        )}
      </div>
    </motion.button>
  );
}

// Icons

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function CarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 17h.01M16 17h.01M6 11h12M3 17h1a2 2 0 002-2v-2.5l1.5-3A2 2 0 019.3 8h5.4a2 2 0 011.8 1.1l1.5 3V15a2 2 0 002 2h1" />
    </svg>
  );
}

function CameraIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}
